import * as React from 'react';
import { css } from 'aphrodite';

import * as autoForm from 'login-utils/autoForm';
import * as autoLoanUtils from 'login-utils/autoLoanUtils';
import AutoLeaseBuyoutSVG from 'login-components/Common/svg/AutoLeaseBuyoutSVG';
import AutoPurchaseSVG from 'login-components/Common/svg/AutoPurchaseSVG';
import AutoRefinanceSVG from 'login-components/Common/svg/AutoRefinanceSVG';
import { DataShape, Field, FormProps, reduxForm } from 'redux-form';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import { connect } from 'react-redux';
import { hashHistory } from 'react-router';
import LargeIconButtonGroup from 'login-components/Common/LargeIconButtonGroup/LargeIconButtonGroup';
import { LargeIconButtonConfig } from 'login-components/Common/Buttons/LargeIconButton';
import LoadingSpinner from 'login-components/Common/LoadingSpinner/LoadingSpinner';
import { paneFormStyles } from 'login-components/Common/PaneLayout/paneStyles';
import States from 'login-constants/states';

const autoLoanPurposeConfigs: LargeIconButtonConfig[] = [
  {
    value: 'PURCHASE',
    iconComponent: AutoPurchaseSVG,
    buttonId: 'it-chooseAutoLoanPurposePurchase',
    text: 'Purchase',
    subText: 'Buy a new or used car.',
  },
  {
    value: 'REFINANCE',
    iconComponent: AutoRefinanceSVG,
    buttonId: 'it-chooseAutoLoanPurposeRefinance',
    text: 'Refinance',
    subText: 'Change your loan terms, get cash out, or both.',
  },
  {
    value: 'LEASE_BUYOUT',
    iconComponent: AutoLeaseBuyoutSVG,
    buttonId: 'it-chooseAutoLoanPurposeBuyout',
    text: 'Lease buyout',
    subText: 'Buy the car you’re leasing from the dealership.',
  },
];

const initialValues: DataShape = {
  autoLoanPurposeType: null,
  autoRefinanceType: null,
  autoVehicleCondition: null,
};

export type ChooseAutoLoanPurposeProps = ReactRouter.RouteComponentProps<{}, {}>;

interface ChooseAutoLoanPurposeState {
  isSubmitting: boolean;
  error: string;
}

export class ChooseAutoLoanPurpose extends React.Component<
  ChooseAutoLoanPurposeProps & FormProps<DataShape, ChooseAutoLoanPurposeProps, ReduxState>,
  ChooseAutoLoanPurposeState
> {
  state = {
    isSubmitting: false,
    error: '',
  };

  onSelect = async value => {
    if (value === 'PURCHASE') {
      hashHistory.push(States.AUTO_PURCHASE_TYPE);
    } else if (value === 'REFINANCE') {
      hashHistory.push(States.AUTO_REFINANCE_TYPE);
    } else if (autoLoanUtils.shouldCreateOrUpdateAutoLoan()) {
      this.setState({
        error: '',
        isSubmitting: true,
      });
      try {
        await autoLoanUtils.createOrUpdateAutoLoan({
          autoLoanPurposeType: value,
        });
      } catch (err) {
        this.setState({
          error: 'Failed to create application, please try again',
          // Only unset on error, since we navigate away in the success case
          // and don't want the user to be able to submit again
          isSubmitting: false,
        });
      }
    } else {
      hashHistory.push(States.CREATE_ACCOUNT);
    }
  };

  render() {
    const { error, isSubmitting } = this.state;
    return (
      <div>
        {isSubmitting ? (
          <div className={css(paneFormStyles.loadingSpinnerWrapper)}>
            <LoadingSpinner />
          </div>
        ) : (
          <React.Fragment>
            {error && (
              <div className={css(paneFormStyles.errorWrapper)}>
                <ErrorMessage errorText={error} />
              </div>
            )}
            <Field
              name={autoForm.FIELDS.autoLoanPurposeType}
              configs={autoLoanPurposeConfigs}
              onSelect={this.onSelect}
              component={LargeIconButtonGroup}
            />
          </React.Fragment>
        )}
      </div>
    );
  }
}

const withReduxForm = reduxForm<
  FormProps<DataShape, ChooseAutoLoanPurposeProps, ReduxState>,
  ChooseAutoLoanPurposeProps,
  ReduxState
>({
  initialValues,
  ...autoForm.FORM_CONFIG,
})(ChooseAutoLoanPurpose);

export default connect<{}, {}, ChooseAutoLoanPurposeProps>(null, {})(withReduxForm);
